// ==============================================================================
// FILE: src/pages/UserManagementPage.tsx
// ==============================================================================

import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Paper, Button, Dialog, DialogTitle,
    DialogContent, TextField, DialogActions, FormControl, InputLabel, Select, MenuItem, Chip, IconButton, Tooltip,
    useTheme, useMediaQuery
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Add as AddIcon, Person as PersonIcon, AdminPanelSettings as AdminIcon, Edit as EditIcon, Delete as DeleteIcon, Security as SuperAdminIcon } from '@mui/icons-material';
import { useAppContext } from '../contexts/AppContext';
import { User, UserCreateAdmin, UserUpdate, SystemRole } from '../types';

export default function UserManagementPage(): JSX.Element {
    const { apiService, user: loggedInUser, showNotification, isSuperAdmin } = useAppContext();
    const [loading, setLoading] = useState<boolean>(true);
    const [users, setUsers] = useState<User[]>([]);

    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    
    const [newUser, setNewUser] = useState<UserCreateAdmin>({ email: '', display_name: '', role: 'reviewer' });
    const [currentUser, setCurrentUser] = useState<User | null>(null);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const fetchUsers = () => {
        setLoading(true);
        apiService.getAllUsers()
            .then(setUsers)
            .catch((err) => showNotification(err.message || 'Failed to fetch user list.', 'error'))
            .finally(() => setLoading(false));
    };

    useEffect(() => { fetchUsers(); }, []);

    const handleAddOpen = () => setAddDialogOpen(true);
    
    const handleEditOpen = (userToEdit: User) => {
        setCurrentUser(userToEdit);
        setEditDialogOpen(true);
    };

    const handleDeleteOpen = (userToDelete: User) => {
        setCurrentUser(userToDelete);
        setDeleteDialogOpen(true);
    };

    const handleClose = () => {
        setAddDialogOpen(false);
        setEditDialogOpen(false);
        setDeleteDialogOpen(false);
        setCurrentUser(null);
        setNewUser({ email: '', display_name: '', role: 'reviewer' });
    };

    const handleCreateUser = async () => {
        if (!newUser.email || !newUser.display_name) {
            showNotification('Email and Display Name are required.', 'warning');
            return;
        }
        try {
            await apiService.createUser(newUser);
            showNotification(`User ${newUser.email} created successfully.`, 'success');
            handleClose();
            fetchUsers();
        } catch (err: any) {
            showNotification(err.message || 'Failed to create user.', 'error');
        }
    };

    const handleUpdateUser = async () => {
        if (!currentUser) return;
        try {
            const updateData: UserUpdate = { display_name: currentUser.display_name, role: currentUser.role };
            await apiService.updateUser(currentUser.id, updateData);
            showNotification(`User ${currentUser.email} updated successfully.`, 'success');
            handleClose();
            fetchUsers();
        } catch (err: any) {
            showNotification(err.message || 'Failed to update user.', 'error');
        }
    };

    const handleDeleteUser = async () => {
        if (!currentUser) return;
        try {
            await apiService.deleteUser(currentUser.id);
            showNotification(`User ${currentUser.email} deleted successfully.`, 'success');
            handleClose();
            fetchUsers();
        } catch (err: any) {
            showNotification(err.message || 'Failed to delete user.', 'error');
        }
    };

    const columns: GridColDef[] = [
        { field: 'display_name', headerName: 'Display Name', flex: 1, minWidth: 180 },
        { field: 'email', headerName: 'Email', flex: 1, minWidth: 220 },
        { field: 'role', headerName: 'Role', width: 150, renderCell: (params) => {
            let icon = <PersonIcon />;
            let color = 'primary';
            if (params.value === 'admin') {
                icon = <AdminIcon />;
                color = 'secondary';
            } else if (params.value === 'super_admin') {
                icon = <SuperAdminIcon />;
                color = 'success';
            }
            return (<Chip icon={icon} label={params.value} color={color as "primary" | "secondary" | "error" | "info" | "success" | "warning"} variant="outlined" size="small" />)
        }},
        {
            field: 'actions',
            headerName: 'Actions',
            width: 120,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: GridRenderCellParams<User>) => (
                <Box>
                    <Tooltip title="Edit User">
                        <IconButton onClick={() => handleEditOpen(params.row)}><EditIcon /></IconButton>
                    </Tooltip>
                    <Tooltip title="Delete User">
                        <span>
                            <IconButton onClick={() => handleDeleteOpen(params.row)} disabled={params.row.id === loggedInUser?.id}>
                                <DeleteIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            )
        }
    ];

    return (
        <Paper sx={{ p: { xs: 2, md: 3 }, height: 'calc(100vh - 110px)', width: '100%' }} elevation={3}>
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: { xs: 'flex-start', sm: 'space-between' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                mb: 2,
                gap: 2
            }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 0 }}>
                    User Management
                </Typography>
                <Button 
                    variant="contained" 
                    startIcon={<AddIcon />} 
                    onClick={handleAddOpen}
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                    Add New User
                </Button>
            </Box>
            {/* FIX: Wrap the DataGrid in a responsive container that allows horizontal scrolling. */}
            <Box sx={{ height: { xs: 'calc(100% - 150px)', sm: 'calc(100% - 100px)' } , width: '100%', overflowX: 'auto' }}>
                <DataGrid rows={users} columns={columns} getRowId={(row) => row.id} loading={loading} disableSelectionOnClick />
            </Box>

            <Dialog open={addDialogOpen} onClose={handleClose} fullWidth maxWidth="sm">
                <DialogTitle>Add New User</DialogTitle>
                <DialogContent>
                    <TextField autoFocus margin="dense" label="Display Name" type="text" fullWidth variant="outlined" value={newUser.display_name} onChange={(e) => setNewUser({...newUser, display_name: e.target.value})} sx={{mt: 1}} />
                    <TextField margin="dense" label="Email Address" type="email" fullWidth variant="outlined" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} />
                    <FormControl fullWidth margin="dense">
                        <InputLabel>Role</InputLabel>
                        <Select label="Role" value={newUser.role} onChange={(e) => setNewUser({...newUser, role: e.target.value as SystemRole})}>
                            <MenuItem value="reviewer">Reviewer</MenuItem>
                            <MenuItem value="admin">Admin</MenuItem>
                            {isSuperAdmin() && <MenuItem value="super_admin">Super Admin</MenuItem>}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleCreateUser} variant="contained">Create User</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={editDialogOpen} onClose={handleClose} fullWidth maxWidth="sm">
                <DialogTitle>Edit User: {currentUser?.display_name}</DialogTitle>
                <DialogContent>
                    {currentUser && (
                        <>
                            <TextField autoFocus margin="dense" label="Display Name" type="text" fullWidth variant="outlined" value={currentUser.display_name} onChange={(e) => setCurrentUser({...currentUser, display_name: e.target.value})} sx={{mt: 1}} />
                            <FormControl fullWidth margin="dense">
                                <InputLabel>Role</InputLabel>
                                <Select label="Role" value={currentUser.role} onChange={(e) => setCurrentUser({...currentUser, role: e.target.value as SystemRole})} disabled={currentUser.id === loggedInUser?.id}>
                                    <MenuItem value="reviewer">Reviewer</MenuItem>
                                    <MenuItem value="admin">Admin</MenuItem>
                                    {isSuperAdmin() && <MenuItem value="super_admin">Super Admin</MenuItem>}
                                </Select>
                            </FormControl>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleUpdateUser} variant="contained">Save Changes</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteDialogOpen} onClose={handleClose} fullWidth maxWidth="xs">
                <DialogTitle>Delete User</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete user "{currentUser?.display_name}"?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleDeleteUser} variant="contained" color="error">Delete</Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
}
